import React, { useState } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { Modal, Input } from 'antd';
import {
  arrowBackOutline,
  mailOutline,
  trashOutline,
  warningOutline,
  closeOutline,
} from 'ionicons/icons';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../lib/firebase/firebase';
import { EmailAuthProvider, reauthenticateWithCredential, signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../lib/firebase/firebase';
import RevenueCatService from '../../lib/revenuecat/RevenueCatService';
import { isNativeiOS } from '../../lib/utils/platform';
import './SettingsPage.css';

const functions = getFunctions(app);

const AccountSettingsPage = ({ onBack }) => {
  const { user } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState('confirm');
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [deleteProgress, setDeleteProgress] = useState('');

  const CONFIRM_PHRASE = 'DELETE';

  const handleDeleteAccount = async () => {
    const firebaseUser = auth.currentUser;

    if (!user || !user.uid || !firebaseUser) {
      setError('User session not found. Please refresh and try again.');
      return;
    }

    // Step 1: confirm screen -> move to type-to-confirm
    if (deleteStep === 'confirm') {
      setDeleteStep('typeConfirm');
      setError('');
      return;
    }

    // Step 2: type-to-confirm -> validate phrase, then move to password or deletion
    if (deleteStep === 'typeConfirm') {
      if (confirmText.trim().toUpperCase() !== CONFIRM_PHRASE) {
        setError(`Please type "${CONFIRM_PHRASE}" exactly to continue.`);
        return;
      }
      setError('');

      let hasPassword = false;
      try {
        hasPassword =
          firebaseUser.providerData &&
          Array.isArray(firebaseUser.providerData) &&
          firebaseUser.providerData.some((p) => p && p.providerId === 'password');
      } catch {
        hasPassword = false;
      }

      if (hasPassword) {
        setDeleteStep('password');
        return;
      }
      // Social login — skip password, fall through to deletion below
    }

    setIsDeleting(true);
    setError('');
    setDeleteProgress('');

    let hasPasswordProvider = false;
    try {
      hasPasswordProvider =
        firebaseUser.providerData &&
        Array.isArray(firebaseUser.providerData) &&
        firebaseUser.providerData.some((p) => p && p.providerId === 'password');
    } catch {
      hasPasswordProvider = false;
    }

    try {
      // Re-authenticate if using password provider
      if (hasPasswordProvider) {
        if (!password) {
          setDeleteStep('password');
          setIsDeleting(false);
          return;
        }

        setDeleteProgress('Verifying your identity...');
        const credential = EmailAuthProvider.credential(firebaseUser.email, password);
        await reauthenticateWithCredential(firebaseUser, credential);
        await new Promise((r) => setTimeout(r, 500));
        await firebaseUser.getIdToken(true);
      }

      setDeleteStep('deleting');

      // Logout RevenueCat on iOS before the backend deletes the auth user
      if (isNativeiOS()) {
        setDeleteProgress('Cleaning up purchase data...');
        try {
          const rc = RevenueCatService.getInstance();
          await rc.logout();
        } catch (e) {
          console.log('RevenueCat logout error (continuing):', e.message);
        }
      }

      // Call backend Cloud Function — handles all Firestore, Storage, Stripe,
      // and Auth deletion server-side for reliability
      setDeleteProgress('Deleting your account and all data...');
      const deleteAccountFunc = httpsCallable(functions, 'deleteUserAccount');
      await deleteAccountFunc();

      // Explicitly sign out to immediately clear the local session and
      // trigger onAuthStateChanged — don't rely on automatic token expiry
      // detection which can take up to an hour on iOS.
      setDeleteProgress('Done! Signing you out...');
      try {
        await signOut(auth);
      } catch (_) {
        // signOut may throw if the auth user is already gone server-side;
        // that's fine — the local state will still be cleared.
      }
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
        setDeleteStep('password');
      } else if (error.code === 'auth/requires-recent-login') {
        if (hasPasswordProvider && password) {
          setError('Authentication failed. Please double-check your password.');
          setDeleteStep('password');
        } else {
          const providers = firebaseUser?.providerData?.map((p) => p.providerId) || [];
          const name = providers.includes('google.com')
            ? 'Google'
            : providers.includes('apple.com')
            ? 'Apple'
            : 'your provider';
          setError(
            `For security, you must log out and log back in with ${name} before deleting your account. Then try again immediately.`
          );
          setDeleteStep('confirm');
        }
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a few minutes and try again.');
        setDeleteStep('confirm');
      } else if (error.code === 'functions/unauthenticated') {
        setError('Your session has expired. Please sign in again and retry.');
        setDeleteStep('confirm');
      } else {
        const msg = error?.details?.message || error?.message || 'Failed to delete account. Please contact support.';
        setError(msg);
        setDeleteStep('confirm');
      }
    } finally {
      setIsDeleting(false);
      setDeleteProgress('');
    }
  };

  const resetDialog = () => {
    if (isDeleting) return;
    setDeleteDialogOpen(false);
    setDeleteStep('confirm');
    setConfirmText('');
    setPassword('');
    setError('');
    setDeleteProgress('');
  };

  return (
    <div className="settings-page">
      <div className="sp-header">
        <button className="sp-back-btn" onClick={onBack}>
          <IonIcon icon={arrowBackOutline} />
        </button>
        <h2 className="sp-header-title">Account Settings</h2>
      </div>

      <div className="sp-scroll">
        {/* Account Information */}
        <div className="sp-section">
          <p className="sp-section-title">Account Information</p>
          <div className="sp-item sp-item--static">
            <div
              className="sp-item-icon"
              style={{ background: '#eff6ff', color: '#3b82f6' }}
            >
              <IonIcon icon={mailOutline} />
            </div>
            <div className="sp-item-body">
              <p className="sp-item-sub" style={{ margin: 0 }}>Email Address</p>
              <p className="sp-item-title">{user?.email || 'Not available'}</p>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="sp-section sp-section--danger">
          <p className="sp-section-title sp-section-title--danger">Danger Zone</p>
          <div className="sp-info-box sp-info-box--warning">
            <IonIcon icon={warningOutline} className="sp-info-box-icon" style={{ color: '#d97706' }} />
            <div>
              <p className="sp-info-box-text">
                <strong>Warning: Account deletion is permanent and cannot be undone.</strong>
              </p>
              <p className="sp-info-box-text" style={{ marginTop: 4 }}>
                This will permanently delete your account, saved files, and all associated data.
              </p>
            </div>
          </div>
          <div style={{ padding: '0 18px 18px' }}>
            <button className="sp-btn-danger" onClick={() => setDeleteDialogOpen(true)}>
              <IonIcon icon={trashOutline} style={{ fontSize: 16 }} />
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Modal
        open={deleteDialogOpen}
        onCancel={resetDialog}
        footer={null}
        width={480}
        className="sp-dialog"
        centered
        destroyOnClose
        maskClosable={!isDeleting}
        closable={!isDeleting}
      >
        <div className="sp-dialog-header">
          <div className="sp-dialog-header-left">
            <IonIcon icon={warningOutline} style={{ fontSize: 22, color: '#ef4444' }} />
            <h3 className="sp-dialog-title">Delete Account</h3>
          </div>
          {!isDeleting && (
            <button className="sp-dialog-close" onClick={resetDialog}>
              <IonIcon icon={closeOutline} />
            </button>
          )}
        </div>

        <div style={{ padding: 24 }}>
          {deleteStep === 'confirm' && (
            <>
              <p style={{ fontSize: 15, color: '#1e293b', marginBottom: 16 }}>
                Are you sure you want to delete your account? This action cannot be undone.
              </p>
              <div className="sp-info-box sp-info-box--danger" style={{ margin: 0 }}>
                <IonIcon icon={warningOutline} className="sp-info-box-icon" style={{ color: '#ef4444' }} />
                <p className="sp-info-box-text">
                  <strong>This will permanently:</strong><br />
                  • Delete your account and all associated data<br />
                  • Remove all your saved files and conversion history<br />
                  • Cancel your subscription and remove premium access
                </p>
              </div>
              {error && (
                <div className="sp-info-box sp-info-box--danger" style={{ marginTop: 12 }}>
                  <p className="sp-info-box-text" style={{ color: '#ef4444', whiteSpace: 'pre-line' }}>{error}</p>
                </div>
              )}
            </>
          )}

          {deleteStep === 'typeConfirm' && (
            <>
              <p style={{ fontSize: 15, color: '#1e293b', marginBottom: 8 }}>
                To confirm, type <strong style={{ color: '#ef4444', fontFamily: 'monospace', letterSpacing: 1 }}>{CONFIRM_PHRASE}</strong> below:
              </p>
              <Input
                placeholder={`Type ${CONFIRM_PHRASE} to confirm`}
                value={confirmText}
                onChange={(e) => { setConfirmText(e.target.value); setError(''); }}
                size="large"
                status={error ? 'error' : confirmText && confirmText.trim().toUpperCase() === CONFIRM_PHRASE ? '' : undefined}
                style={{
                  borderRadius: 10,
                  fontFamily: 'monospace',
                  fontSize: 16,
                  letterSpacing: 2,
                  textAlign: 'center',
                }}
                autoFocus
              />
              {error && (
                <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{error}</p>
              )}
              <div className="sp-info-box sp-info-box--warning" style={{ margin: '14px 0 0' }}>
                <IonIcon icon={warningOutline} className="sp-info-box-icon" style={{ color: '#d97706' }} />
                <p className="sp-info-box-text">
                  This is your last chance to cancel. After this step, your account and all data will be permanently erased.
                </p>
              </div>
            </>
          )}

          {deleteStep === 'password' && (
            <>
              <p style={{ fontSize: 15, color: '#1e293b', marginBottom: 16 }}>
                Please enter your password to confirm account deletion:
              </p>
              <Input.Password
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                size="large"
                status={error ? 'error' : undefined}
                disabled={isDeleting}
                style={{ borderRadius: 10 }}
              />
              {error && (
                <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{error}</p>
              )}
            </>
          )}

          {deleteStep === 'deleting' && (
            <>
              <p style={{ fontSize: 15, color: '#1e293b', marginBottom: 12 }}>
                Deleting your account and all associated data...
              </p>
              <div className="sp-delete-progress">
                <IonSpinner name="lines" style={{ width: 22, height: 22, color: '#667eea' }} />
                <span className="sp-delete-progress-text">
                  {deleteProgress || 'Processing...'}
                </span>
              </div>
              <div className="sp-info-box sp-info-box--info" style={{ marginTop: 12 }}>
                <p className="sp-info-box-text">
                  Please don't close this dialog while we're deleting your data.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="sp-dialog-footer" style={{ gap: 12 }}>
          <button className="sp-btn-secondary" onClick={resetDialog} disabled={isDeleting}>
            Cancel
          </button>
          <button
            className="sp-btn-danger"
            onClick={handleDeleteAccount}
            disabled={
              isDeleting ||
              (deleteStep === 'typeConfirm' && confirmText.trim().toUpperCase() !== CONFIRM_PHRASE) ||
              (deleteStep === 'password' && !password)
            }
          >
            {isDeleting ? (
              <>
                <IonSpinner name="lines" style={{ width: 16, height: 16 }} />
                Deleting...
              </>
            ) : deleteStep === 'confirm' ? (
              'Continue'
            ) : (
              <>
                <IonIcon icon={trashOutline} style={{ fontSize: 16 }} />
                Permanently Delete
              </>
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AccountSettingsPage;
